import React from "react";
import PropTypes from "prop-types";
import neoLogo from "../../assets/images/Neo_Logo_A.svg";

/**
 * PoolPrintableReportLayout
 * Reusable layout component mirroring the swimming workout (modo piscina) print layout.
 * Ensures identical typography, A4 format, border accents, and branded headers/footers.
 */
const PoolPrintableReportLayout = React.forwardRef(({
    tenantName,
    tenantLogo,
    title,
    meta,
    details = [],
    footerNote = "NeoApp • modo piscina",
    showTenantOnLeft = true,
    titleSize = "18px",
    children
}, ref) => {
    return (
        <div 
            ref={ref}
            className="pool-print-page"
            style={{ 
                padding: "40px", 
                fontFamily: "'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif", 
                color: "#17212b", 
                backgroundColor: "#fff",
                minHeight: "297mm",
                boxSizing: "border-box"
            }}
        >
            {/* Embedded styles to guarantee consistent styles in preview and prints */}
            <style>{`
                .pool-print-page {
                    background: #fff !important;
                    color: #17212b !important;
                    font-family: "Inter", "Helvetica Neue", Arial, sans-serif !important;
                    padding: 40px !important;
                    box-sizing: border-box !important;
                }

                .pool-print-header {
                    display: flex !important;
                    align-items: center !important;
                    justify-content: space-between !important;
                    gap: 24px !important;
                    border-bottom: 3px solid #1266a8 !important;
                    padding-bottom: 14px !important;
                    margin-bottom: 14px !important;
                }

                .pool-print-brand {
                    display: flex !important;
                    align-items: center !important;
                    gap: 12px !important;
                }

                .pool-print-brand img {
                    max-width: 96px !important;
                    max-height: 54px !important;
                    object-fit: contain !important;
                }

                .pool-print-brand span,
                .pool-print-meta span,
                .pool-print-summary span {
                    display: block !important;
                    color: #5f6c7b !important;
                    font-size: 11px !important;
                    font-weight: 700 !important;
                    text-transform: uppercase !important;
                }

                .pool-print-brand strong {
                    display: block !important;
                    color: #111827 !important;
                    font-size: 22px !important;
                    line-height: 1.1 !important;
                }

                .pool-print-meta {
                    text-align: right !important;
                    font-size: 13px !important;
                    color: #444 !important;
                }

                .pool-print-meta strong {
                    display: inline !important;
                    font-size: 13px !important;
                    color: #111 !important;
                }

                .pool-print-block {
                    break-inside: avoid !important;
                    page-break-inside: avoid !important;
                    margin-bottom: 25px !important;
                }

                .pool-print-block-title {
                    display: flex !important;
                    justify-content: space-between !important;
                    align-items: center !important;
                    background: #17212b !important;
                    color: #fff !important;
                    padding: 8px 12px !important;
                    margin-bottom: 10px !important;
                }

                .pool-print-block-title strong {
                    font-size: 14px !important;
                    color: #fff !important;
                    font-weight: 800 !important;
                }

                .pool-print-block-title span {
                    font-size: 12px !important;
                    font-weight: 850 !important;
                }

                .pool-print-table {
                    width: 100% !important;
                    border-collapse: collapse !important;
                    font-size: 12px !important;
                    margin-top: 10px !important;
                }

                .pool-print-table th {
                    background: #eef6ff !important;
                    color: #24445f !important;
                    border: 1px solid #d9e8fb !important;
                    padding: 8px 10px !important;
                    text-align: left !important;
                    font-weight: 600 !important;
                    font-size: 12px !important;
                }

                .pool-print-table td {
                    border: 1px solid #e3e7ee !important;
                    padding: 8px 10px !important;
                    vertical-align: top !important;
                    font-size: 12px !important;
                }

                .pool-print-table tr {
                    page-break-inside: avoid !important;
                    break-inside: avoid !important;
                }

                .pool-print-footer {
                    display: flex !important;
                    align-items: center !important;
                    justify-content: space-between !important;
                    border-top: 1px solid #e3e7ee !important;
                    margin-top: 50px !important;
                    padding-top: 15px !important;
                    color: #94a3b8 !important;
                    font-size: 11px !important;
                    text-transform: none !important;
                }

                .pool-print-footer img {
                    height: 14px !important;
                    filter: grayscale(100%) opacity(0.55) !important;
                }

                @media print {
                    .pool-print-page {
                        padding: 12mm !important;
                    }

                    .pool-print-block {
                        break-inside: avoid !important;
                        page-break-inside: avoid !important;
                    }
                }
            `}</style>

            {/* CABEÇALHO DO MODO PISCINA */}
            <div className="pool-print-header">
                <div className="pool-print-brand">
                    {tenantLogo && (
                        <img src={tenantLogo} alt="Logo" />
                    )}
                    <div>
                        {showTenantOnLeft && (
                            <span>{tenantName || "Equipe / Professor"}</span>
                        )}
                        <strong style={{ fontSize: titleSize }}>{title || "Relatório"}</strong>
                    </div>
                </div>
                <div className="pool-print-meta">
                    {meta}
                </div>
            </div>

            {/* DETALHES COMPACTOS DO MODO PISCINA (SUB-HEADER A4) */}
            {details && details.length > 0 && (
                <div style={{ 
                    fontSize: "12px", 
                    color: "#444", 
                    backgroundColor: "#f8f9fa", 
                    padding: "10px 14px", 
                    border: "1px solid #e9ecef", 
                    borderRadius: "4px",
                    marginBottom: "20px",
                    lineHeight: "1.5"
                }}>
                    {details.map((detail, idx) => {
                        if (!detail) return null;
                        return (
                            <div 
                                key={idx}
                                style={{
                                    borderTop: idx > 0 ? "1px dotted #ccc" : "none",
                                    paddingTop: idx > 0 ? "6px" : "0",
                                    marginTop: idx > 0 ? "6px" : "0",
                                    fontSize: idx > 0 ? "11px" : "12px",
                                    color: idx > 0 ? "#666" : "#444"
                                }}
                            >
                                {detail.content}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* CONTEÚDO PRINCIPAL DO RELATÓRIO */}
            <div className="pool-print-content">
                {children}
            </div>

            {/* RODAPÉ MODO PISCINA */}
            <div className="pool-print-footer">
                <span style={{ fontStyle: "italic" }}>
                    {footerNote}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span style={{ fontWeight: "700", color: "#aaa", letterSpacing: "1px", fontSize: "11px" }}>POWERED BY</span>
                    <img src={neoLogo} alt="NeoApp" />
                </div>
            </div>
        </div>
    );
});

PoolPrintableReportLayout.displayName = "PoolPrintableReportLayout";

PoolPrintableReportLayout.propTypes = {
    tenantName: PropTypes.string,
    tenantLogo: PropTypes.string,
    title: PropTypes.string.isRequired,
    meta: PropTypes.node,
    details: PropTypes.arrayOf(PropTypes.shape({
        content: PropTypes.node.isRequired
    })),
    footerNote: PropTypes.string,
    showTenantOnLeft: PropTypes.bool,
    titleSize: PropTypes.string,
    children: PropTypes.node
};

export default PoolPrintableReportLayout;
